import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  HANDOVER_DOC_TITLES,
  HANDOVER_KIND_LABELS,
  HANDOVER_SECTIONS,
  HANDOVER_STATUS_LABELS,
  HandoverFieldDef,
  HandoverKind,
  HandoverStatus,
} from "@/lib/basProcess/constants";
import { FileSignature, Plus, Trash2, FileDown, RefreshCw } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import { generateHandoverWordDocument } from "@/lib/documentGenerator/overlamningWordGenerator";

interface Props {
  projectId: string;
}

type SectionContent = Record<string, any>;

interface HandoverDoc {
  id: string;
  kind: HandoverKind;
  name: string;
  status: string;
  stage_id: string | null;
  content_json: Record<string, SectionContent>;
}

interface StageOption {
  id: string;
  name: string;
}

const isFilled = (field: HandoverFieldDef, value: any): boolean => {
  if (field.type === "checklist") return Object.values(value || {}).some(Boolean);
  if (field.type === "signature")
    return Object.values(value || {}).some((v: any) => (v?.name || v?.date || v?.sign || "").trim?.());
  return typeof value === "string" && value.trim().length > 0;
};

export const HandoverDocuments: React.FC<Props> = ({ projectId }) => {
  const [docs, setDocs] = useState<HandoverDoc[]>([]);
  const [stages, setStages] = useState<StageOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    const [{ data, error }, stageRes] = await Promise.all([
      supabase
        .from("handover_documents")
        .select("id, kind, name, status, stage_id, content_json")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false }),
      supabase.from("project_stages").select("id, name").eq("project_id", projectId).order("sort_order"),
    ]);
    if (error) toast.error("Kunde inte läsa överlämningsdokumenten");
    else setDocs((data || []).map((d: any) => ({ ...d, content_json: d.content_json || {} })));
    setStages((stageRes.data as StageOption[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  /** Hämtar statusläget för Dok 34 direkt från projektets data */
  const buildSummary = async (): Promise<Record<string, SectionContent>> => {
    const [risks, followups, qa, amp] = await Promise.all([
      supabase.from("residual_use_risks").select("id, status").eq("project_id", projectId),
      supabase.from("use_phase_followups").select("id, space_name").eq("project_id", projectId),
      supabase.from("qa_log_entries").select("id, status").eq("project_id", projectId),
      supabase
        .from("work_environment_plans")
        .select("name, version, status")
        .eq("project_id", projectId)
        .order("updated_at", { ascending: false })
        .limit(1),
    ]);

    const riskRows = risks.data || [];
    const openRisks = riskRows.filter((r: any) => r.status === "open").length;

    const followupIds = (followups.data || []).map((f: any) => f.id);
    let verified = 0;
    let deviations = 0;
    let items: any[] = [];
    if (followupIds.length) {
      const { data } = await supabase
        .from("use_phase_followup_items")
        .select("status, control_point, responsible, checked_at, comment, followup_id")
        .in("followup_id", followupIds);
      items = data || [];
      verified = items.filter((i) => i.status === "verified").length;
      deviations = items.filter((i) => i.status === "deviation").length;
    }

    const openQa = (qa.data || []).filter((e: any) => e.status === "open").length;
    const plan = (amp.data || [])[0] as any;

    const deviationLines = items
      .filter((i) => i.status === "deviation")
      .map(
        (i) =>
          `• ${i.control_point}${i.responsible ? ` – ansvarig: ${i.responsible}` : ""}${
            i.checked_at ? ` (${i.checked_at})` : ""
          }${i.comment ? ` – ${i.comment}` : ""}`
      )
      .join("\n");

    return {
      statuslage: {
        bruksrisker: `${riskRows.length} kvarvarande bruksrisker registrerade, varav ${openRisks} fortfarande öppna.`,
        uppfoljning: `${followupIds.length} uppföljda utrymmen, ${items.length} kontrollpunkter: ${verified} verifierade, ${deviations} avvikelser.`,
        qalogg: `${openQa} öppna frågor i Q&A-loggen av totalt ${(qa.data || []).length}.`,
        amp: plan
          ? `${plan.name} – version ${plan.version}, status ${plan.status}.`
          : "Ingen arbetsmiljöplan upprättad i projektet.",
      },
      skyddsrond: deviationLines ? { punkter: deviationLines } : {},
    };
  };

  const create = async (kind: HandoverKind) => {
    const content_json =
      kind === "slutsammanfattning" ? await buildSummary() : ({} as Record<string, SectionContent>);
    const { data, error } = await supabase
      .from("handover_documents")
      .insert({
        project_id: projectId,
        kind,
        name: HANDOVER_DOC_TITLES[kind],
        content_json: content_json as any,
      })
      .select("id")
      .single();
    if (error) return toast.error("Kunde inte skapa dokumentet");
    await load();
    setOpenId(data.id);
  };

  const update = async (id: string, patch: Partial<HandoverDoc>) => {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    const { error } = await supabase.from("handover_documents").update(patch as any).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    setDocs((prev) => prev.filter((d) => d.id !== id));
    await supabase.from("handover_documents").delete().eq("id", id);
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar överlämningar...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="05 / 20 / 22 / 34"
        title="Överlämnings- och uppdragsdokument"
        description="Byggherrens skriftliga utseende av Bas-P och Bas-U, överlämning Bas-P till Bas-U, överlämning till byggherre samt överlämningssammanfattning inför bruksskedet."
        icon={FileSignature}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => create("utseende_basp")}>
              <Plus className="h-4 w-4 mr-1" />Utse Bas-P
            </Button>
            <Button variant="outline" onClick={() => create("utseende_basu")}>
              <Plus className="h-4 w-4 mr-1" />Utse Bas-U
            </Button>
            <Button variant="outline" onClick={() => create("basu")}>
              <Plus className="h-4 w-4 mr-1" />Bas-P → Bas-U
            </Button>
            <Button variant="outline" onClick={() => create("byggherre")}>
              <Plus className="h-4 w-4 mr-1" />Till byggherre
            </Button>
            <Button onClick={() => create("slutsammanfattning")}>
              <Plus className="h-4 w-4 mr-1" />Överlämningssammanfattning
            </Button>
          </div>
        }
      />

      {docs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <FileSignature className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Inget överlämningsdokument upprättat ännu.</p>
          </CardContent>
        </Card>
      ) : (
        docs.map((d) => {
          const isOpen = openId === d.id;
          const sections = HANDOVER_SECTIONS[d.kind] || HANDOVER_SECTIONS.basu;
          const content = d.content_json || {};
          const total = sections.reduce((n, s) => n + s.fields.length, 0);
          const filled = sections.reduce(
            (n, s) => n + s.fields.filter((f) => isFilled(f, content[s.key]?.[f.key])).length,
            0
          );

          const setField = (sectionKey: string, fieldKey: string, value: any) =>
            update(d.id, {
              content_json: {
                ...content,
                [sectionKey]: { ...(content[sectionKey] || {}), [fieldKey]: value },
              },
            });

          return (
            <Card key={d.id}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button className="text-left" onClick={() => setOpenId(isOpen ? null : d.id)}>
                    <CardTitle className="text-base">{d.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {HANDOVER_KIND_LABELS[d.kind]} · {filled}/{total} fält ifyllda
                      {d.stage_id
                        ? ` · ${stages.find((s) => s.id === d.stage_id)?.name ?? "Etapp"}`
                        : stages.length
                          ? " · ingen etapp vald"
                          : ""}
                    </p>
                  </button>
                  <div className="flex items-center gap-2">
                    <Badge variant={d.status === "draft" ? "outline" : "default"}>
                      {HANDOVER_STATUS_LABELS[(d.status as HandoverStatus) ?? "draft"] || "Utkast"}
                    </Badge>
                    {d.kind === "slutsammanfattning" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          const s = await buildSummary();
                          await update(d.id, {
                            content_json: {
                              ...content,
                              statuslage: { ...(content.statuslage || {}), ...s.statuslage },
                              skyddsrond: { ...(content.skyddsrond || {}), ...(s.skyddsrond || {}) },
                            },
                          });
                          toast.success("Siffrorna uppdaterade");
                        }}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />Uppdatera siffror
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => update(d.id, { status: d.status === "draft" ? "signed" : "draft" })}
                    >
                      {d.status === "draft" ? "Markera överlämnad" : "Återöppna"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateHandoverWordDocument(d.name, d.kind, content)}
                    >
                      <FileDown className="h-4 w-4 mr-1" />Word
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(d.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {isOpen && (
                <CardContent className="space-y-6">
                  <Input defaultValue={d.name} onBlur={(e) => update(d.id, { name: e.target.value || "Överlämning" })} />

                  <div className="grid gap-3 rounded-lg border border-border/60 p-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <p className="text-sm font-semibold">Etapp</p>
                      <p className="text-xs text-muted-foreground">
                        Koppla överlämningen till rätt etapp så att tidplan, risker och kvittens hänger ihop.
                      </p>
                      <Select
                        value={d.stage_id ?? "none"}
                        onValueChange={(v) => update(d.id, { stage_id: v === "none" ? null : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Välj etapp" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Hela projektet</SelectItem>
                          {stages.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <p className="text-sm font-semibold">Kvittens från mottagaren</p>
                      <p className="text-xs text-muted-foreground">
                        Överlämningen räknas som fullgjord först när mottagaren kvitterar att materialet är mottaget.
                      </p>
                      <Input
                        placeholder="Mottagarens namn"
                        defaultValue={content.kvittens?.mottagare || ""}
                        onBlur={(e) => setField("kvittens", "mottagare", e.target.value)}
                      />
                      {content.kvittens?.datum ? (
                        <div className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs">
                          <span>
                            Kvitterad av {content.kvittens?.mottagare || "mottagare"} den {content.kvittens.datum}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              await update(d.id, {
                                status: "signed",
                                content_json: {
                                  ...content,
                                  kvittens: { ...(content.kvittens || {}), datum: "" },
                                },
                              });
                            }}
                          >
                            Ångra
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          disabled={d.status === "draft"}
                          onClick={async () => {
                            const namn = (content.kvittens?.mottagare || "").trim();
                            if (!namn) return toast.error("Ange mottagarens namn först");
                            await update(d.id, {
                              status: "acknowledged",
                              content_json: {
                                ...content,
                                kvittens: {
                                  ...(content.kvittens || {}),
                                  mottagare: namn,
                                  datum: new Date().toISOString().slice(0, 10),
                                },
                              },
                            });
                            toast.success("Kvittens registrerad");
                          }}
                        >
                          Kvittera mottagen
                        </Button>
                      )}
                      {d.status === "draft" && (
                        <p className="text-xs text-muted-foreground">
                          Markera dokumentet som överlämnat innan mottagaren kan kvittera.
                        </p>
                      )}
                    </div>
                  </div>


                  {sections.map((s, si) => (
                    <div key={s.key} className="space-y-3 rounded-lg border border-border/60 p-4">
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold">
                          {si + 1}. {s.title}
                        </h4>
                        {s.intro?.map((p, i) => (
                          <p key={i} className="text-xs leading-relaxed text-muted-foreground">
                            {p}
                          </p>
                        ))}
                        {s.bullets && (
                          <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
                            {s.bullets.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        )}
                        {s.outro?.map((p, i) => (
                          <p key={i} className="text-xs leading-relaxed text-muted-foreground">
                            {p}
                          </p>
                        ))}
                      </div>

                      {s.fields.length > 0 && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          {s.fields.map((f) => {
                            const value = content[s.key]?.[f.key];
                            const wide = f.type === "textarea" || f.type === "checklist" || f.type === "signature";
                            return (
                              <div key={f.key} className={`space-y-1 ${wide ? "sm:col-span-2" : ""}`}>
                                <label className="text-sm font-medium">{f.label}</label>
                                {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}

                                {f.type === "checklist" ? (
                                  <div className="space-y-2 pt-1">
                                    {(f.items || []).map((item) => (
                                      <label key={item} className="flex items-start gap-2 text-sm">
                                        <Checkbox
                                          checked={Boolean(value?.[item])}
                                          onCheckedChange={(c) =>
                                            setField(s.key, f.key, { ...(value || {}), [item]: Boolean(c) })
                                          }
                                        />
                                        <span className="leading-snug">{item}</span>
                                      </label>
                                    ))}
                                  </div>
                                ) : f.type === "signature" ? (
                                  <div className="space-y-3 pt-1">
                                    {(f.parties || []).map((party) => (
                                      <div key={party} className="rounded-md border border-border/60 p-3 space-y-2">
                                        <p className="text-xs font-semibold">{party}</p>
                                        <div className="grid gap-2 sm:grid-cols-3">
                                          <Input
                                            placeholder="Namn"
                                            defaultValue={value?.[party]?.name ?? ""}
                                            onBlur={(e) =>
                                              setField(s.key, f.key, {
                                                ...(value || {}),
                                                [party]: { ...(value?.[party] || {}), name: e.target.value },
                                              })
                                            }
                                          />
                                          <Input
                                            type="date"
                                            defaultValue={value?.[party]?.date ?? ""}
                                            onBlur={(e) =>
                                              setField(s.key, f.key, {
                                                ...(value || {}),
                                                [party]: { ...(value?.[party] || {}), date: e.target.value },
                                              })
                                            }
                                          />
                                          <Input
                                            placeholder="Signatur"
                                            defaultValue={value?.[party]?.sign ?? ""}
                                            onBlur={(e) =>
                                              setField(s.key, f.key, {
                                                ...(value || {}),
                                                [party]: { ...(value?.[party] || {}), sign: e.target.value },
                                              })
                                            }
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : f.type === "textarea" ? (
                                  <Textarea
                                    rows={3}
                                    defaultValue={value ?? ""}
                                    onBlur={(e) => setField(s.key, f.key, e.target.value)}
                                  />
                                ) : (
                                  <Input
                                    type={f.type === "date" ? "date" : "text"}
                                    defaultValue={value ?? ""}
                                    onBlur={(e) => setField(s.key, f.key, e.target.value)}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
};

export default HandoverDocuments;
